import { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';

import { queryClient } from '@/store/queryClient';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { WebSocketProvider } from '@/websocket/WebSocketProvider';

import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { EventListPage } from '@/pages/EventListPage';
import { EventCreatePage } from '@/pages/EventCreatePage';
import { EventDetailPage } from '@/pages/EventDetailPage';
import { EventEditPage } from '@/pages/EventEditPage';
import { UploadPage } from '@/pages/UploadPage';
import { GalleryPage } from '@/pages/GalleryPage';
import { PhotoDetailPage } from '@/pages/PhotoDetailPage';
import { FaceWizardPage } from '@/pages/FaceWizardPage';
import { PersonDirectoryPage } from '@/pages/PersonDirectoryPage';
import { PersonDetailPage } from '@/pages/PersonDetailPage';
import { AnalysisDashboardPage } from '@/pages/AnalysisDashboardPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { CullingPage } from '@/pages/CullingPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<LoadingFallback />}>
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected routes with AppShell layout */}
              <Route
                element={
                  <ProtectedRoute>
                    <WebSocketProvider>
                      <AppShell />
                    </WebSocketProvider>
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="events" element={<EventListPage />} />
                <Route path="events/create" element={<EventCreatePage />} />
                <Route path="events/:id" element={<EventDetailPage />} />
                <Route path="events/:id/edit" element={<EventEditPage />} />
                <Route path="upload" element={<UploadPage />} />
                <Route path="events/:id/upload" element={<UploadPage />} />
                <Route path="gallery" element={<GalleryPage />} />
                <Route path="culling" element={<CullingPage />} />
                <Route path="photos/:id" element={<PhotoDetailPage />} />
                <Route path="faces" element={<FaceWizardPage />} />
                <Route path="people" element={<PersonDirectoryPage />} />
                <Route path="people/:id" element={<PersonDetailPage />} />
                <Route path="analysis" element={<AnalysisDashboardPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>

              {/* 404 catch-all */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
        </Suspense>

        <Toaster
          position="bottom-right"
          richColors
          closeButton
          toastOptions={{
            duration: 4000,
          }}
        />

        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
